import {
  SYNC_CONCURRENCY_HELP,
  isRegisteredPreviewSend,
  type DecisionEngineDestinationDefinition,
  type SyncSpec,
} from "./definition";
import { hightouchPayloadMapping } from "./sync-config-helpers";

function buildBrazeAdditionalConfig({
  isPreview,
  sendToHightouchUser,
  previewRecipientMode,
}: Pick<
  SyncSpec,
  "isPreview" | "sendToHightouchUser" | "previewRecipientMode"
>) {
  // Registered mode targets the customer-registered account profile and must
  // never create or mutate Braze records, so it uses the exact production
  // shape: sendToExistingOnly stays true and no attribute mappings are sent.
  if (isRegisteredPreviewSend({ isPreview, previewRecipientMode })) {
    return {
      userMapping: {
        type: "standard",
        from: "external_id",
        to: "external_user_id",
      },
      sendToExistingOnly: true,
    };
  }
  if (isPreview && sendToHightouchUser) {
    return {
      sendToExistingOnly: false,
      attributeMappings: [
        { type: "standard", from: "external_id", to: "email" },
        {
          type: "standard",
          from: "is_preview",
          to: "is_hightouch_preview_user",
        },
      ],
      userMapping: { type: "standard", from: "id", to: "external_user_id" },
    };
  }
  return {
    userMapping: {
      type: "standard",
      from: "external_id",
      to: "external_user_id",
    },
    ...(isPreview
      ? {
          sendToExistingOnly: false,
          attributeMappings: [
            {
              type: "standard",
              from: "is_preview",
              to: "is_hightouch_preview_user",
            },
          ],
        }
      : { sendToExistingOnly: true }),
  };
}

// Braze is the one AID destination where `mode` is customer-tunable: AID does
// not emit it, and the rETL trigger sync treats it as pure CDC semantics
// (which diff rows fire triggers) rather than a load-bearing literal. Batch
// size is hardcoded to 50 in the destination; parallelization is read from
// config at runtime (default 10) with no rETL form cap, so 100 just keeps
// overrides sane.
const brazeSyncTuning = {
  sleepMs: {
    defaultValue: 0,
    min: 0,
    max: 5000,
    label: "Delay between batches (ms)",
    description: "Defaults to 0 ms (no added delay).",
    help: "Pause after each batch to slow sends to the destination. Higher values make syncs take longer.",
  },
  modes: {
    defaultValue: "upsert",
    label: "Send triggers",
    description:
      "Choose which changes in sync rows trigger a campaign or Canvas send.",
    options: [
      { value: "insert", label: "New rows" },
      { value: "update", label: "Changed rows" },
      { value: "upsert", label: "New and changed rows" },
    ],
  },
  parallelization: {
    defaultValue: 10,
    max: 100,
    label: "Concurrent requests",
    description:
      "Controls how many campaign or Canvas send requests run in parallel, with up to 50 recipients per request.",
    help: SYNC_CONCURRENCY_HELP,
  },
} as const;

export const brazeTriggerDefinition: DecisionEngineDestinationDefinition = {
  destinationType: "braze",
  category: "trigger",
  syncTuning: brazeSyncTuning,
  generateSyncConfig: ({
    producer,
    isPreview,
    sendToHightouchUser,
    previewRecipientMode,
    messageTags,
  }) => ({
    type: "trigger",
    campaignId: { type: "standard", from: "campaign_id" },
    mappings: [hightouchPayloadMapping({ producer, messageTags })],
    ...buildBrazeAdditionalConfig({
      isPreview,
      sendToHightouchUser,
      previewRecipientMode,
    }),
  }),
};

export const brazeCanvasDefinition: DecisionEngineDestinationDefinition = {
  destinationType: "braze",
  // "trigger" refers to the destination framework category (trigger/event/batch),
  // not the Braze campaign type. Canvas uses the trigger framework.
  category: "trigger",
  syncTuning: brazeSyncTuning,
  generateSyncConfig: ({
    producer,
    isPreview,
    sendToHightouchUser,
    previewRecipientMode,
    messageTags,
  }) => ({
    type: "canvas",
    canvasId: { type: "standard", from: "campaign_id" },
    mappings: [hightouchPayloadMapping({ producer, messageTags })],
    ...buildBrazeAdditionalConfig({
      isPreview,
      sendToHightouchUser,
      previewRecipientMode,
    }),
  }),
};
